import { useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/Avatar";
import type { Member } from "@/lib/api";

/** Sentinel for "tickets with no assignee" in the filter set. */
export const UNASSIGNED = "__unassigned__";

/**
 * Assignee filter as a dropdown multi-select: click the trigger to open a list
 * of the team (plus "Unassigned"); tick one or more to filter to their tickets.
 */
export function AssigneeFilter({
  members,
  selected,
  onChange,
}: {
  members: Member[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  // Trigger label reflects the current selection.
  const label =
    selected.size === 0
      ? "All assignees"
      : selected.size === 1
        ? selected.has(UNASSIGNED)
          ? "Unassigned"
          : members.find((m) => selected.has(m.id))?.name ?? "1 selected"
        : `${selected.size} selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-2 rounded-token-md border px-3 py-2 text-sm transition-colors ${
          selected.size > 0
            ? "border-accent text-text-heading"
            : "border-border text-text-secondary hover:border-accent hover:text-text-heading"
        }`}
      >
        <i className="fa-solid fa-user-group text-xs text-text-muted" />
        <span className="max-w-[140px] truncate">{label}</span>
        <i className="fa-solid fa-chevron-down text-[0.6rem] text-text-muted" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1 max-h-80 w-60 overflow-y-auto rounded-token-md border border-border bg-bg-card py-1 shadow-token-lg"
        >
          {members.map((m) => {
            const active = selected.has(m.id);
            return (
              <button
                key={m.id}
                role="option"
                aria-selected={active}
                onClick={() => toggle(m.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-card-hover"
              >
                <Check active={active} />
                <Avatar name={m.name} url={m.avatarUrl} size={22} />
                <span className="truncate text-text">{m.name}</span>
              </button>
            );
          })}

          <button
            role="option"
            aria-selected={selected.has(UNASSIGNED)}
            onClick={() => toggle(UNASSIGNED)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-card-hover"
          >
            <Check active={selected.has(UNASSIGNED)} />
            <span className="grid h-[22px] w-[22px] place-items-center rounded-full border border-dashed border-border text-[10px] text-text-muted">
              <i className="fa-solid fa-user-slash" />
            </span>
            <span className="text-text">Unassigned</span>
          </button>

          {selected.size > 0 && (
            <div className="mt-1 border-t border-border-light pt-1">
              <button
                onClick={() => onChange(new Set())}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-text-muted transition-colors hover:bg-bg-card-hover hover:text-text-heading"
              >
                <i className="fa-solid fa-xmark w-[18px] text-center text-xs" />
                Clear filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A small checkbox-style indicator for the option's selected state. */
function Check({ active }: { active: boolean }) {
  return (
    <span
      className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded border transition-colors ${
        active ? "border-accent text-white" : "border-border text-transparent"
      }`}
      style={active ? { background: "var(--color-accent)", color: "var(--color-on-accent)" } : undefined}
    >
      <i className="fa-solid fa-check text-[0.55rem]" />
    </span>
  );
}
